import { useState, useEffect, useCallback } from 'react';
import { Search, Building2, Download, X, MapPin, Key, Wrench, Flame, Zap, Gauge, Wifi, Trash2, AlertCircle, FileText, Plus, Edit2, Save, XCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const HauslistePage = ({ user }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [loadError, setLoadError] = useState(null);
  
  // CRUD states
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editData, setEditData] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  
  // Export state
  const [exportLoading, setExportLoading] = useState(false);

  // Admin check from user prop or localStorage
  const isAdmin = user?.role === 'admin' || (() => {
    try {
      const savedUser = localStorage.getItem('gb-sylt-user');
      if (savedUser) {
        const parsed = JSON.parse(savedUser);
        return parsed.user?.role === 'admin';
      }
    } catch (e) {}
    return false;
  })();

  const getToken = () => {
    const savedUser = localStorage.getItem('gb-sylt-user');
    if (savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        return parsed.token;
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  const loadHausliste = useCallback(async (search = '') => {
    const token = getToken();
    if (!token) {
      setLoadError({ status: 401, message: 'Nicht angemeldet. Bitte erneut einloggen.' });
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError(null);
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await axios.get(`${API}/hausliste${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const loadedEntries = response.data.entries || [];
      setEntries(loadedEntries);
      
      // Auto-select first entry if none selected
      if (loadedEntries.length > 0 && !selectedEntry) {
        setSelectedEntry(loadedEntries[0]);
      }
    } catch (error) {
      const status = error.response?.status || 0;
      const detail = error.response?.data?.detail || error.message || 'Unbekannter Fehler';
      console.error(`[Hausliste] Fehler beim Laden: status=${status} detail=${detail} url=${API}/hausliste`);
      
      let message;
      if (status === 401) {
        message = 'Sitzung abgelaufen oder Benutzer nicht gefunden. Bitte erneut einloggen.';
        // Forced logout bei 401 - globaler App-Key
        try {
          localStorage.removeItem('gb-sylt-user');
        } catch (e) { /* ignore */ }
      } else if (status === 403) {
        message = 'Keine Berechtigung für die Hausliste.';
      } else if (status === 503) {
        message = 'Datenbank nicht verfügbar. Bitte später erneut versuchen.';
      } else if (status >= 500) {
        message = `Serverfehler (${status}). Bitte später erneut versuchen.`;
      } else if (status === 0) {
        message = 'Verbindung zum Server fehlgeschlagen. Bitte Netzwerk prüfen.';
      } else {
        message = `Fehler beim Laden (Status ${status}): ${detail}`;
      }
      
      setLoadError({ status, message });
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [selectedEntry]);

  useEffect(() => {
    loadHausliste();
  }, []);

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
  };

  // CRUD Functions - available to ALL authenticated users
  const handleCreate = () => {
    try {
      setIsCreating(true);
      setIsEditing(false);
      setEditData({
        schluessel_nr: '',
        strasse_ort: '',
        schluessel_code: '',
        hausverwaltung: '',
        hausmeister: '',
        sanitaer_heizung: '',
        heizung: '',
        elektro: '',
        standort_zaehler: '',
        standort_router: '',
        standort_muelltonnen: '',
        rauchmelder: '',
        infos_sonstiges: '',
      });
      setSelectedEntry(null);
      setSaveStatus(null);
    } catch (error) {
      console.error('Error in handleCreate:', error);
      setSaveStatus({ error: true, message: 'Fehler beim Öffnen des Formulars' });
    }
  };

  const handleEdit = () => {
    try {
      if (!selectedEntry) return;
      setIsEditing(true);
      setIsCreating(false);
      setEditData({ ...selectedEntry });
      setSaveStatus(null);
    } catch (error) {
      console.error('Error in handleEdit:', error);
      setSaveStatus({ error: true, message: 'Fehler beim Laden der Daten' });
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setIsCreating(false);
    setEditData({});
    setSaveStatus(null);
  };

  const handleSave = async () => {
    const token = getToken();
    if (!token) {
      setSaveStatus({ error: true, message: 'Nicht authentifiziert. Bitte erneut anmelden.' });
      return;
    }

    // Validate required field
    if (!editData.schluessel_nr || editData.schluessel_nr.trim() === '') {
      setSaveStatus({ error: true, message: 'Schlüssel-Nr. ist erforderlich' });
      return;
    }

    setSaveStatus({ loading: true });

    try {
      if (isCreating) {
        // Create new entry
        const createResponse = await axios.post(`${API}/hausliste`, editData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (createResponse.status !== 200 && createResponse.status !== 201) {
          throw new Error('Erstellen fehlgeschlagen');
        }
        
        setSaveStatus({ success: true, message: 'Objekt erstellt!' });
        
        // Reload list
        await loadHausliste();
        
        // Find and select the newly created entry
        try {
          const newEntries = await axios.get(`${API}/hausliste`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const created = newEntries.data.entries?.find(e => e.schluessel_nr === editData.schluessel_nr);
          if (created) setSelectedEntry(created);
        } catch (e) {
          console.warn('Could not auto-select new entry:', e);
        }
      } else {
        // Update existing entry
        if (!selectedEntry?.id) {
          throw new Error('Keine Objekt-ID gefunden');
        }
        
        const updateResponse = await axios.put(`${API}/hausliste/${selectedEntry.id}`, editData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (updateResponse.status !== 200) {
          throw new Error('Aktualisierung fehlgeschlagen');
        }
        
        setSaveStatus({ success: true, message: 'Änderungen gespeichert!' });
        
        // Reload and update selected
        await loadHausliste();
        setSelectedEntry({ ...selectedEntry, ...editData });
      }

      // Reset edit mode after success
      setTimeout(() => {
        setIsEditing(false);
        setIsCreating(false);
        setEditData({});
        setSaveStatus(null);
      }, 1500);
    } catch (error) {
      console.error('Save error:', error);
      let errorMessage = 'Speichern fehlgeschlagen';
      
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setSaveStatus({
        error: true,
        message: errorMessage
      });
    }
  };

  const handleDelete = async () => {
    const token = getToken();
    if (!token || !selectedEntry) return;

    try {
      await axios.delete(`${API}/hausliste/${selectedEntry.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowDeleteConfirm(false);
      setSelectedEntry(null);
      loadHausliste();
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Löschen fehlgeschlagen: ' + (error.response?.data?.detail || 'Unbekannter Fehler'));
    }
  };

  // Export function - Admin only
  const handleExport = async (format = 'csv') => {
    const token = getToken();
    if (!token || !isAdmin) return;

    setExportLoading(true);
    try {
      const response = await axios.get(`${API}/hausliste/export?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from header or generate
      const contentDisposition = response.headers['content-disposition'];
      let filename = `hausliste_export.${format}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export fehlgeschlagen: ' + (error.response?.data?.detail || 'Unbekannter Fehler'));
    } finally {
      setExportLoading(false);
    }
  };

  const handleFieldChange = (key, value) => {
    setEditData(prev => ({ ...prev, [key]: value }));
  };

  // Filter entries based on search — durchsucht ALLE Stammdaten-Felder
  const STAMMDATEN_KEYS = [
    'strasse_ort', 'schluessel_nr', 'schluessel_code',
    'hausverwaltung', 'hausmeister', 'sanitaer_heizung', 'heizung', 'elektro',
    'standort_zaehler', 'standort_router', 'standort_muelltonnen',
    'rauchmelder', 'infos_sonstiges', 'gaertner',
  ];
  
  // Lesbare Feldnamen für Treffer-Anzeige
  const STAMMDATEN_LABELS = {
    strasse_ort: 'Adresse', schluessel_nr: 'Schlüssel-Nr.', schluessel_code: 'Schlüssel-Code',
    hausverwaltung: 'Hausverwaltung', hausmeister: 'Hausmeister',
    sanitaer_heizung: 'Sanitär/Heizung', heizung: 'Heizung', elektro: 'Elektro',
    standort_zaehler: 'Standort Zähler', standort_router: 'Standort Router',
    standort_muelltonnen: 'Standort Mülltonnen', rauchmelder: 'Rauchmelder',
    infos_sonstiges: 'Infos/Sonstiges', gaertner: 'Gärtner',
  };

  const filteredEntries = searchQuery.trim() === '' 
    ? entries 
    : (() => {
        const q = searchQuery.toLowerCase();
        return entries.filter(entry =>
          STAMMDATEN_KEYS.some(key => {
            const val = entry[key];
            return val && String(val).toLowerCase().includes(q);
          })
        );
      })();

  // Berechne Treffer-Felder pro Eintrag (nur bei aktiver Suche)
  const getMatchFields = (entry) => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();
    return STAMMDATEN_KEYS.filter(key => {
      const val = entry[key];
      return val && String(val).toLowerCase().includes(q);
    });
  };

  // Detail field configuration
  const detailFields = [
    { key: 'schluessel_nr', label: 'Schlüssel-Nr.', icon: Key, required: true },
    { key: 'schluessel_code', label: 'Schlüssel-Code', icon: Key },
    { key: 'hausverwaltung', label: 'Hausverwaltung', icon: Building2 },
    { key: 'hausmeister', label: 'Hausmeister', icon: Wrench },
    { key: 'sanitaer_heizung', label: 'Sanitär / Heizung', icon: Wrench },
    { key: 'heizung', label: 'Heizung', icon: Flame },
    { key: 'elektro', label: 'Elektro', icon: Zap },
    { key: 'standort_zaehler', label: 'Standort Zähler', icon: Gauge },
    { key: 'standort_router', label: 'Standort Router', icon: Wifi },
    { key: 'standort_muelltonnen', label: 'Standort Mülltonnen', icon: Trash2 },
    { key: 'rauchmelder', label: 'Rauchmelder', icon: AlertCircle },
    { key: 'infos_sonstiges', label: 'Infos / Sonstiges', icon: FileText },
  ];

  const isEditMode = isEditing || isCreating;

  return (
    <div className="h-full flex bg-gray-900" data-testid="hausliste-page">
      {/* Left Column - Object Navigator */}
      <div className="w-80 flex-shrink-0 border-r border-gray-700 flex flex-col bg-gray-850">
        {/* Header with Search */}
        <div className="p-4 border-b border-gray-700">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="h-5 w-5 text-blue-400" />
            <h2 className="text-lg font-semibold text-white">Objekte</h2>
            <span className="text-sm text-gray-400">({filteredEntries.length})</span>
          </div>
          
          {/* Search Field */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Suche nach Adresse, Schlüssel-Nr., Kontakt..."
              value={searchQuery}
              onChange={handleSearch}
              className="w-full pl-9 pr-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="hausliste-search"
            />
          </div>
          
          {/* Action Buttons - CRUD for ALL users, Export only for Admin */}
          <div className="mt-3 flex gap-2">
            <button
              onClick={handleCreate}
              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors"
              data-testid="create-object-btn"
            >
              <Plus className="h-4 w-4" />
              Neues Objekt
            </button>
            
            {/* Export Button - Admin Only */}
            {isAdmin && (
              <div className="relative group">
                <button
                  onClick={() => handleExport('csv')}
                  disabled={exportLoading}
                  className="flex items-center justify-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm rounded-lg border border-gray-700 transition-colors disabled:opacity-50"
                  data-testid="export-btn"
                  title="Objekte exportieren"
                >
                  <Download className="h-4 w-4" />
                  {exportLoading ? '...' : ''}
                </button>
                
                {/* Export dropdown */}
                <div className="absolute right-0 top-full mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 min-w-[140px]">
                  <button
                    onClick={() => handleExport('csv')}
                    className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-700 rounded-t-lg"
                  >
                    Export als CSV
                  </button>
                  <button
                    onClick={() => handleExport('xlsx')}
                    className="w-full text-left px-3 py-2 text-sm text-gray-300 hover:bg-gray-700 rounded-b-lg"
                  >
                    Export als Excel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Object List */}
        <div className="flex-1 overflow-y-auto">
          {loadError ? (
            <div className="p-4" data-testid="hausliste-error-banner">
              <div className="bg-red-900/30 border border-red-700 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-red-300 font-medium text-sm">Fehler beim Laden</p>
                    <p className="text-red-400 text-xs mt-1">{loadError.message}</p>
                    {loadError.status > 0 && (
                      <p className="text-red-500 text-xs mt-1">Status: {loadError.status}</p>
                    )}
                    <button
                      onClick={() => loadHausliste(searchQuery)}
                      className="mt-3 text-xs bg-red-800 hover:bg-red-700 text-red-200 px-3 py-1.5 rounded transition-colors"
                      data-testid="hausliste-retry-button"
                    >
                      Erneut versuchen
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : loading ? (
            <div className="p-4 text-center">
              <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
              <p className="text-gray-400 text-sm">Lade...</p>
            </div>
          ) : filteredEntries.length === 0 ? (
            <div className="p-4 text-center text-gray-500 text-sm">
              {searchQuery ? 'Keine Treffer' : 'Keine Objekte vorhanden'}
            </div>
          ) : (
            <div className="py-1">
              {filteredEntries.map((entry) => (
                <button
                  key={entry.id}
                  onClick={() => {
                    if (!isEditMode) {
                      setSelectedEntry(entry);
                    }
                  }}
                  className={`w-full text-left px-4 py-3 border-b border-gray-800 transition-colors ${
                    selectedEntry?.id === entry.id
                      ? 'bg-blue-600 text-white'
                      : isEditMode 
                        ? 'text-gray-500 cursor-not-allowed'
                        : 'text-gray-300 hover:bg-gray-800'
                  }`}
                  disabled={isEditMode}
                  data-testid={`hausliste-item-${entry.schluessel_nr}`}
                >
                  <div className="font-medium text-sm truncate">
                    {entry.strasse_ort || 'Keine Adresse'}
                  </div>
                  <div className={`text-xs mt-0.5 ${
                    selectedEntry?.id === entry.id ? 'text-blue-200' : 'text-gray-500'
                  }`}>
                    Nr. {entry.schluessel_nr}
                  </div>
                  {searchQuery.trim() && (() => {
                    const matchKeys = getMatchFields(entry);
                    if (matchKeys.length === 0) return null;
                    const labels = matchKeys.map(k => STAMMDATEN_LABELS[k] || k).join(', ');
                    return (
                      <div className={`text-xs mt-1 truncate ${
                        selectedEntry?.id === entry.id ? 'text-blue-200' : 'text-yellow-500/70'
                      }`} data-testid={`match-info-${entry.schluessel_nr}`}>
                        Treffer in: {labels}
                      </div>
                    );
                  })()}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right Column - Detail View / Edit Form */}
      <div className="flex-1 overflow-y-auto bg-gray-900">
        {(selectedEntry || isCreating) ? (
          <div className="p-6">
            {/* Header */}
            <div className="mb-6 pb-4 border-b border-gray-700">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center">
                    <MapPin className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    {isEditMode ? (
                      <input
                        type="text"
                        value={editData.strasse_ort || ''}
                        onChange={(e) => handleFieldChange('strasse_ort', e.target.value)}
                        placeholder="Straße / Ort"
                        className="text-xl font-bold text-white bg-gray-800 border border-gray-600 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        data-testid="edit-strasse-ort"
                      />
                    ) : (
                      <h1 className="text-xl font-bold text-white">
                        {selectedEntry?.strasse_ort || 'Keine Adresse'}
                      </h1>
                    )}
                    <p className="text-gray-400 text-sm mt-1">
                      {isCreating ? 'Neues Objekt' : `Schlüssel-Nr. ${selectedEntry?.schluessel_nr}`}
                    </p>
                  </div>
                </div>

                {/* Action Buttons - Available to ALL users */}
                <div className="flex gap-2">
                  {isEditMode ? (
                    <>
                      <button
                        onClick={handleSave}
                        disabled={saveStatus?.loading}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
                        data-testid="save-object-btn"
                      >
                        <Save className="h-4 w-4" />
                        {saveStatus?.loading ? 'Speichern...' : 'Speichern'}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors"
                        data-testid="cancel-edit-btn"
                      >
                        <XCircle className="h-4 w-4" />
                        Abbrechen
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={handleEdit}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors"
                        data-testid="edit-object-btn"
                      >
                        <Edit2 className="h-4 w-4" />
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => setShowDeleteConfirm(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg transition-colors"
                        data-testid="delete-object-btn"
                      >
                        <Trash2 className="h-4 w-4" />
                        Löschen
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* Save Status Message */}
              {saveStatus && (
                <div className={`mt-4 p-3 rounded-lg ${
                  saveStatus.loading ? 'bg-blue-500/20 text-blue-400' :
                  saveStatus.success ? 'bg-green-500/20 text-green-400' :
                  'bg-red-500/20 text-red-400'
                }`}>
                  {saveStatus.message || (saveStatus.loading ? 'Speichern...' : '')}
                </div>
              )}
            </div>

            {/* Detail Fields */}
            <div className="space-y-4">
              <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">
                Stammdaten
              </h2>
              
              <div className="grid gap-3">
                {detailFields.map((field) => {
                  const IconComponent = field.icon;
                  const value = isEditMode ? editData[field.key] : selectedEntry?.[field.key];
                  const hasValue = value && value !== 'None' && value.toString().trim() !== '';
                  
                  return (
                    <div
                      key={field.key}
                      className="bg-gray-800 rounded-lg p-4 border border-gray-700"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <IconComponent className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-400">
                          {field.label}
                          {field.required && isEditMode && <span className="text-red-400 ml-1">*</span>}
                        </span>
                      </div>
                      {isEditMode ? (
                        <input
                          type="text"
                          value={value || ''}
                          onChange={(e) => handleFieldChange(field.key, e.target.value)}
                          placeholder={`${field.label} eingeben...`}
                          className="w-full text-sm text-white bg-gray-700 border border-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          data-testid={`edit-${field.key}`}
                        />
                      ) : (
                        <div className={`text-sm ${hasValue ? 'text-white' : 'text-gray-600 italic'}`}>
                          {hasValue ? value : 'Noch kein Inhalt vorhanden'}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Metadata */}
              {!isEditMode && selectedEntry && (
                <div className="mt-8 pt-4 border-t border-gray-700">
                  <p className="text-xs text-gray-600">
                    {selectedEntry.created_at && (
                      <>Erstellt: {new Date(selectedEntry.created_at).toLocaleDateString('de-DE')}</>
                    )}
                    {selectedEntry.created_by && (
                      <> von {selectedEntry.created_by}</>
                    )}
                    {selectedEntry.updated_at && selectedEntry.updated_at !== selectedEntry.created_at && (
                      <> · Aktualisiert: {new Date(selectedEntry.updated_at).toLocaleDateString('de-DE')}</>
                    )}
                    {selectedEntry.updated_by && selectedEntry.updated_at !== selectedEntry.created_at && (
                      <> von {selectedEntry.updated_by}</>
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="h-full flex items-center justify-center">
            <div className="text-center text-gray-500">
              <Building2 className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Wählen Sie ein Objekt aus der Liste</p>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                <Trash2 className="h-5 w-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-white">Objekt löschen?</h2>
            </div>
            
            <p className="text-gray-400 mb-2">
              Möchten Sie dieses Objekt wirklich löschen?
            </p>
            <p className="text-white font-medium mb-4">
              {selectedEntry?.strasse_ort} (Nr. {selectedEntry?.schluessel_nr})
            </p>
            <p className="text-red-400 text-sm mb-6">
              Diese Aktion kann nicht rückgängig gemacht werden!
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Abbrechen
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                data-testid="confirm-delete-btn"
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HauslistePage;
